const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8100/api';
const STORAGE_KEY = 'jamsd_auth';
const REQUEST_TIMEOUT_MS = 30000;

export class ApiError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = details.status;
    this.data = details.data;
  }
}

export function getStoredAuth() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
  } catch (error) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function storeAuth(auth) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  window.dispatchEvent(new Event('jamsd-auth-change'));
}

export function clearAuth() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event('jamsd-auth-change'));
}

async function request(path, options = {}) {
  const auth = getStoredAuth();
  const isFormData = options.body instanceof FormData;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      signal: options.signal || controller.signal,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(auth?.token ? { Authorization: `Bearer ${auth.token}` } : {}),
        ...options.headers,
      },
    });
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ApiError('The request timed out. Please check your connection and try again.');
    }
    throw new ApiError('Unable to reach the server. Please check your connection and try again.');
  } finally {
    window.clearTimeout(timeout);
  }

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json().catch(() => ({})) : {};
  if (!response.ok) {
    if (response.status === 401) clearAuth();
    throw new ApiError(data.message || data.error || `Request failed with status ${response.status}.`, {
      status: response.status,
      data,
    });
  }
  return data;
}

export async function registerUser(payload) {
  return request('/auth/register', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function loginUser(payload) {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function oauthLogin(provider, profile = {}) {
  return request('/auth/oauth', {
    method: 'POST',
    body: JSON.stringify({ provider, ...profile }),
  });
}

export async function getMe() {
  return request('/auth/me');
}

export async function updateProfile(payload) {
  return request('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function lookupAuthorByEmail(email) {
  return request(`/auth/authors/lookup?email=${encodeURIComponent(email)}`);
}

export async function createSubmission(formData) {
  return request('/submissions', {
    method: 'POST',
    body: formData,
  });
}

export async function updateSubmission(id, formData) {
  return request(`/submissions/${id}`, {
    method: 'PUT',
    body: formData,
  });
}

export async function listSubmissions(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/submissions${query ? `?${query}` : ''}`);
}

export async function getSubmission(id) {
  return request(`/submissions/${id}`);
}

export async function updateSubmissionStatus(id, payload) {
  return request(`/submissions/${id}/status`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function listArticles(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/articles${query ? `?${query}` : ''}`);
}

export async function getArticle(id) {
  return request(`/articles/${id}`);
}

export async function listIssues(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/issues${query ? `?${query}` : ''}`);
}

export async function getIssue(id) {
  return request(`/issues/${id}`);
}

export async function createIssue(payload, imageFile) {
  if (imageFile) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) formData.append(key, value);
    });
    formData.append('image', imageFile);
    return request('/issues', { method: 'POST', body: formData });
  }
  return request('/issues', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function publishIssue(id) {
  return request(`/issues/${id}/publish`, { method: 'PATCH' });
}

export async function unpublishIssue(id) {
  return request(`/issues/${id}/unpublish`, { method: 'PATCH' });
}

export async function publishSubmission(id, payload, imageFile) {
  if (imageFile) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) formData.append(key, value);
    });
    formData.append('image', imageFile);
    return request(`/submissions/${id}/publish`, { method: 'POST', body: formData });
  }
  return request(`/submissions/${id}/publish`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function assignSubmissionIssue(id, issueId) {
  return request(`/submissions/${id}/assign-issue`, {
    method: 'PATCH',
    body: JSON.stringify({ issueId: issueId || null }),
  });
}

export async function sendContactMessage(payload) {
  return request('/contact', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getAdminDashboard() {
  return request('/admin/dashboard');
}

export async function listAdminSubmissions(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/submissions${query ? `?${query}` : ''}`);
}

function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export async function getGoogleCredential() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) return null;

  await loadGoogleScript();

  return new Promise((resolve, reject) => {
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => resolve(response.credential),
    });

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        reject(new Error('Google sign-in was not completed.'));
      }
    });
  });
}
